import {
	DatePicker,
	DateRangeType,
	DefaultButton,
	defaultCalendarStrings,
	Dialog,
	DialogFooter,
	Icon,
	IPersonaProps,
	ITextFieldStyles,
	Label,
	MessageBar,
	MessageBarType,
	NormalPeoplePicker,
	Pivot,
	PivotItem,
	PrimaryButton,
	ProgressIndicator,
	Shimmer,
	Spinner,
	Stack,
	TextField
} from "@fluentui/react"
import { usePrevious } from "@fluentui/react-hooks"
import { useEffect, useState } from "react"
import { useApiCall } from "../hooks/apiHooks"
import { getErrorText, IS_ADMIN, safeFormatDateValue } from "../lib/helper"
import { API_METHODS, makeACMGCRMApiCall, makeApiCall, makeBrokerAPICall, makeCRMApiCall } from "../services/apiService"
import { CWDepartment, IAccount, INewAccount } from "../types/IAccount"
import { IGroupPlan, IPlanSelectorPlan } from "../types/IGroupPlan"
import { EnrollmentManager } from "./enrollment-manager"
import { GradientCensusForm } from "./gradientCensusDialog"
import { GroupDocuments } from "./group-documents"
import { OpportunityHistory } from "./opportunityHistory"
import { PlanSelector } from "./planSelector"
import { QuoteHistory } from "./quoteHistory"
import { RaterForm } from "./raterDialog"

const today = new Date()
const bufferDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14)
const minDate = new Date(bufferDate.getFullYear(), bufferDate.getMonth() + 1, 1)

interface EditGroupPanelProps {
	initialGroup?: IAccount
	selectedTab?: "groupInfo" | "contactDetails" | "enrollmentManager" | "quoteGenerator"
	closePanel: (refreshItems: boolean) => void
}

function getDefaultState(group?: IAccount, addGroup?: boolean): IAccount {
	const currentUser = (window.top as any).parent.Microsoft.Dynamic365.Portal.User
	const currentUserContactId = (window.top as any).parent.Microsoft.Dynamic365.Portal.User.contactId
	const currentUserEmail = (window.top as any).parent.Microsoft.Dynamic365.Portal.User.userName
	const currentUserName = currentUser.firstName && currentUser.lastName ? `${currentUser.firstName} ${currentUser.lastName}` : currentUserEmail

	return {
		name: group?.name || "",
		address1_city: group?.address1_city || "",
		address1_country: group?.address1_country || "",
		address1_line1: group?.address1_line1 || "",
		address1_postalcode: group?.address1_postalcode || "",
		telephone1: group?.telephone1 || "",
		cw_PreferredBroker: addGroup && !IS_ADMIN ? { contactid: currentUserContactId, fullname: currentUserName } : group?.cw_PreferredBroker || undefined,
		_cw_industry_value: group?._cw_industry_value || undefined,
		address1_stateorprovince: group?.address1_stateorprovince || "",
		plans: group?.plans || [],
		cw_customertype: 2,
		...group,
		cw_coveragestartdate: group?.cw_coveragestartdate ? safeFormatDateValue(group.cw_coveragestartdate, "yyyy-MM-dd", "Invalid Date") : ""
	}
}

const queryForAccount = async (accountId: string) => {
	const accounts = await makeApiCall(`/getGroupsForBroker?accountId=${accountId}`)
	if (accounts.length === 1) {
		return accounts[0]
	}
	await new Promise(resolve => setTimeout(resolve, 500))
	return await queryForAccount(accountId)
}

const h3Styles = {
	marginTop: 8,
	marginBottom: 8
}

export function EditGroupDialog({ initialGroup, closePanel, selectedTab }: EditGroupPanelProps) {
	const [group, setGroup] = useState<IAccount>(initialGroup)
	const [shouldRerefreshMainList, setShouldRerefreshMainList] = useState(false)
	const isProspect = group?.cw_accounttype.includes("Prospect") || !group

	const pivotItems = [
		<PivotItem headerText="Group Information" key="groupInfo" itemKey="groupInfo">
			<PanelForm
				key="groupInfo"
				group={group}
				onSave={async accountId => {
					const account = await queryForAccount(accountId)
					setGroup({ cw_accounttype: "Prospect", ...account })
					setShouldRerefreshMainList(true)
				}}
			/>
		</PivotItem>,
		group && (
			<PivotItem headerText="Contact Details" key="contactDetails" itemKey="contactDetails">
				<ContactsForm group={group} />
			</PivotItem>
		),
		group && (
			<PivotItem headerText="Group Documents" key="quotingRequirements" itemKey="quotingRequirements">
				<GroupDocuments group={group} />
			</PivotItem>
		),
		group && (isProspect || IS_ADMIN) && (
			<PivotItem headerText="Census" key="enrollmentManager" itemKey="enrollmentManager">
				<EnrollmentManager
					group={group}
					closePanel={() => {
						/*gulp*/
					}}
				/>
			</PivotItem>
		),
		group && (
			<PivotItem headerText="Opportunities" key="opportunityHistory" itemKey="opportunityHistory">
				<OpportunityHistory group={group} />
			</PivotItem>
		),
		group && IS_ADMIN && (IS_ADMIN || (!group.cw_lockunderwriting && !group.cw_salelost)) && (
			<PivotItem headerText="Quote Generator" key="quoteGenerator" itemKey="quoteGenerator">
				<RaterForm
					group={group}
					onDismiss={() => {
						closePanel(false)
					}}
					isCRM={false}
				/>
			</PivotItem>
		),
		group && IS_ADMIN && (IS_ADMIN || (!group.cw_lockunderwriting && !group.cw_salelost)) && (
			<PivotItem headerText="Quote History" key="quoteHistory" itemKey="quoteHistory">
				<QuoteHistory group={group} />
			</PivotItem>
		),
		group && IS_ADMIN && (
			<PivotItem headerText="Gradient Census Generator" key="gradientGenerator" itemKey="gradientGenerator">
				<GradientCensusForm
					group={group}
					onDismiss={() => {
						closePanel(false)
					}}
				/>
			</PivotItem>
		)
	].filter(Boolean)

	return (
		<Dialog
			onDismiss={() => {
				closePanel(shouldRerefreshMainList)
			}}
			styles={{
				main: {
					width: "calc(100vw - 180px) !important",
					minWidth: "calc(100vw - 180px) !important",
					maxWidth: "calc(100vw - 180px) !important",
					height: "calc(100vh - 100px)"
				}
			}}
			hidden={false}
			dialogContentProps={{
				title: (
					<Stack horizontalAlign="center">
						<h3 style={{ marginTop: 0 }}>Group: {!group ? "New Prospect" : group.name}</h3>
					</Stack>
				)
			}}
			modalProps={{
				isBlocking: true
			}}>
			<Pivot defaultSelectedKey={selectedTab}>{pivotItems}</Pivot>
		</Dialog>
	)
}

interface PanelFormProps {
	group?: IAccount
	onSave: (accountId: string) => void
}

const handleChange = (field: string, value: any, setForm: (form: React.SetStateAction<Partial<IAccount>>) => void) => setForm(f => ({ ...f, [field]: value }))
// const convertIndustryToPersona = (industry: any) => ({ key: industry.cw_industryid, text: industry.cw_name })
const convertBrokerToPersona = (broker: any) => ({ key: broker.contactid, text: broker.fullname, _cw_brokerage_value: broker._cw_brokerage_value, secondaryText: broker.emailaddress1 })
const convertPersonaToBroker = (persona: IPersonaProps) => ({ contactid: persona.key, fullname: persona.text, _cw_brokerage_value: (persona as any)._cw_brokerage_value })

interface ContactsFormProps {
	group: IAccount
}

interface IDataFields {
	firstname: string
	lastname: string
	fullname: string
	emailaddress1: string
	telephone1: string
	cw_businessphoneextention: string
	mobilephone: string
}

interface IResponseContact extends IDataFields {
	contactid: string
}

interface IContactsFormData {
	primarycontactid: IDataFields
	cw_SecondaryContact: IDataFields
	cw_PlanFiduciaryContact: IDataFields
	cw_BillingContact: IDataFields
	cw_AdminContact: IDataFields
}

interface IAccountResponse {
	primarycontactid: null | IResponseContact
	cw_BillingContact: null | IResponseContact
	cw_PlanFiduciaryContact: null | IResponseContact
	cw_SecondaryContact: null | IResponseContact
	cw_AdminContact: null | IResponseContact
}

type ContactType = keyof IContactsFormData
type FieldName = keyof IDataFields

interface EditContactState {
	name: string
	type: ContactType
}

function ContactsForm(props: ContactsFormProps) {
	const { group } = props

	const selectString = "$select=emailaddress1,firstname,fullname,lastname,telephone1,cw_businessphoneextention,mobilephone"

	const expandKeys = ["primarycontactid", "cw_BillingContact", "cw_PlanFiduciaryContact", "cw_SecondaryContact", "cw_AdminContact"]
	const selectStringExpanded = expandKeys.map(key => `${key}(${selectString})`).join(",")

	const apiUrl = `/accounts(${group.accountid})?$select=accountid&$expand=${selectStringExpanded}`

	const {
		data: accountData,
		loading,
		setData: setAccountData,
		error
	} = useApiCall<IAccountResponse>(apiUrl, {
		crm: true,
		method: "GET",
		canFetch: !!group
	})

	const [formData, setFormData] = useState<IContactsFormData>({
		cw_BillingContact: { firstname: "", lastname: "", emailaddress1: "", fullname: "", telephone1: "", cw_businessphoneextention: "", mobilephone: "" },
		cw_PlanFiduciaryContact: { firstname: "", lastname: "", emailaddress1: "", fullname: "", telephone1: "", cw_businessphoneextention: "", mobilephone: "" },
		cw_SecondaryContact: { firstname: "", lastname: "", emailaddress1: "", fullname: "", telephone1: "", cw_businessphoneextention: "", mobilephone: "" },
		primarycontactid: { firstname: "", lastname: "", emailaddress1: "", fullname: "", telephone1: "", cw_businessphoneextention: "", mobilephone: "" },
		cw_AdminContact: { firstname: "", lastname: "", emailaddress1: "", fullname: "", telephone1: "", cw_businessphoneextention: "", mobilephone: "" }
	})
	const [saving, setSaving] = useState(false)
	const [saveError, setSaveError] = useState<string | undefined>()
	const [editContact, setEditContact] = useState<EditContactState>(null)

	useEffect(() => {
		if (accountData) {
			setFormData({
				cw_BillingContact: accountData.cw_BillingContact,
				cw_PlanFiduciaryContact: accountData.cw_PlanFiduciaryContact,
				cw_SecondaryContact: accountData.cw_SecondaryContact,
				primarycontactid: accountData.primarycontactid,
				cw_AdminContact: accountData.cw_AdminContact
			})
		}
	}, [accountData])

	const onSave = async (contactType: ContactType) => {
		setSaving(true)
		try {
			const formContact = formData[contactType]
			if (formContact && formContact.firstname !== "" && formContact.lastname !== "" && formContact.emailaddress1 !== "") {
				let contactId = accountData?.[contactType]?.contactid
				let method: API_METHODS = accountData[contactType] ? "PATCH" : "POST"
				let shouldUpdateContact = true
				try {
					const existingContactResult = await makeCRMApiCall(
						`contacts(emailaddress1='${formContact.emailaddress1}')?$select=contactid,firstname,lastname,fullname,emailaddress1,telephone1,cw_businessphoneextention,mobilephone`,
						"GET"
					)
					if (existingContactResult?.contactid) {
						contactId = existingContactResult.contactid
						method = "PATCH"
						const fieldsToCompare = ["firstname", "lastname", "telephone1", "cw_businessphoneextention", "mobilephone"]
						const isMatch = fieldsToCompare.every(field => formContact[field] === existingContactResult[field])
						if (isMatch) {
							shouldUpdateContact = false
						}
					}
				} catch (err) {
					/*gulp*/
				}
				if (shouldUpdateContact) {
					const apiPath = contactId ? `contacts(${contactId})` : "contacts"
					const body = {
						firstname: formContact.firstname,
						lastname: formContact.lastname,
						emailaddress1: formContact.emailaddress1,
						fullname: `${formContact.firstname} ${formContact.lastname}`,
						telephone1: formContact.telephone1,
						cw_businessphoneextention: formContact.cw_businessphoneextention,
						mobilephone: formContact.mobilephone
					}
					if (!contactId) {
						body["parentcustomerid_account@odata.bind"] = `/accounts(${group.accountid})`
					}
					const contactResult = await makeBrokerAPICall(group.accountid, apiPath, method, body)
					if (!contactId) {
						contactId = JSON.parse(contactResult).contactid
					}
				}
				await makeBrokerAPICall(group.accountid, `accounts(${group.accountid})`, "PATCH", { [`${contactType}@odata.bind`]: `/contacts(${contactId})` })
				setAccountData(prevData => ({
					...prevData,
					[contactType]: {
						...formContact,
						contactid: contactId
					}
				}))
			}
			setSaving(false)
			setEditContact(undefined)
		} catch (err) {
			setSaving(false)
			setSaveError("There was an error saving the contact information.")
			console.error(err)
		}
	}

	const shouldFieldBeRequired = (contactType: ContactType, fieldName: FieldName) => {
		const currentFieldHasData = formData[contactType] && formData[contactType][fieldName]
		if (fieldName === "cw_businessphoneextention" || fieldName === "mobilephone") {
			return false
		}
		if (fieldName === "telephone1") {
			return contactType === "primarycontactid"
		}
		if (contactType === "primarycontactid") {
			return true
		}
		if (currentFieldHasData) {
			return true
		}
		const otherFieldsInSectionHaveData = Object.keys(formData[contactType] || {})?.some(key => key !== fieldName && formData[contactType]?.[key])
		return otherFieldsInSectionHaveData
	}

	const renderTextField = (label: string, fieldName: FieldName, contactType: ContactType) =>
		loading ? (
			<Shimmer />
		) : (
			<TextField
				key={`${contactType}-${fieldName}`}
				label={label}
				required={shouldFieldBeRequired(contactType, fieldName)}
				value={formData?.[contactType]?.[fieldName] || ""}
				onChange={(_ev, newValue) => {
					setFormData({
						...formData,
						[contactType]: { ...formData[contactType], [fieldName]: newValue || "" }
					})
				}}
				onGetErrorMessage={value => (fieldName === "emailaddress1" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "Please enter a valid email address." : "")}
			/>
		)

	const renderFormSection = (sectionTitle: string, contactType: ContactType) => (
		<Stack tokens={{ childrenGap: 8 }} grow key={contactType}>
			<Label>{sectionTitle}</Label>
			{renderTextField("First Name", "firstname", contactType)}
			{renderTextField("Last Name", "lastname", contactType)}
			{renderTextField("Email", "emailaddress1", contactType)}

			<Stack horizontal tokens={{ childrenGap: 10 }}>
				<Stack grow>{renderTextField("Business Phone", "telephone1", contactType)}</Stack>
				<Stack styles={{ root: { width: 120 } }}>{renderTextField("Extension", "cw_businessphoneextention", contactType)}</Stack>
			</Stack>
			{renderTextField("Mobile Phone", "mobilephone", contactType)}
		</Stack>
	)

	const renderContactCard = (sectionTitle: string, contactType: ContactType) => {
		const contactExists = !!accountData?.[contactType]

		const cardStyle: React.CSSProperties = {
			border: contactExists ? "1px solid #edebe9" : "1px dashed #a19f9d",
			borderRadius: "4px",
			padding: "16px",
			backgroundColor: contactExists ? "#ffffff" : "#f3f2f1",
			boxShadow: contactExists ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
			display: "flex",
			flexDirection: "column",
			justifyContent: "space-between",
			minHeight: "160px",
			minWidth: "215px"
		}

		return (
			<div style={cardStyle}>
				<div>
					<div style={{ fontWeight: "bold", fontSize: "12px", color: "#605e5c", textTransform: "uppercase", marginBottom: "8px" }}>{sectionTitle}</div>
					{contactExists ? (
						<div>
							<h4 style={{ margin: "0 0 4px 0", fontSize: "16px", fontWeight: "bold", color: "#323130" }}>
								{accountData?.[contactType]?.["firstname"]} {accountData?.[contactType]?.["lastname"]}
							</h4>
							<p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#605e5c", wordBreak: "break-all" }}>{accountData?.[contactType]?.["emailaddress1"]}</p>
							{accountData?.[contactType]?.["telephone1"] ? (
								<p style={{ margin: "0 0 4px 0", fontSize: "14px", color: "#605e5c" }}>
									<strong>O:</strong> {accountData?.[contactType]?.["telephone1"]}
									{accountData?.[contactType]?.["cw_businessphoneextention"] && ` ext. ${accountData?.[contactType]?.["cw_businessphoneextention"]}`}
								</p>
							) : (
								<p style={{ margin: "0 0 4px 0", fontSize: "14px", color: "#605e5c" }}>No business phone provided</p>
							)}
							{accountData?.[contactType]?.["mobilephone"] ? (
								<p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#605e5c" }}>
									<strong>M:</strong> {accountData?.[contactType]?.["mobilephone"]}
								</p>
							) : null}
						</div>
					) : (
						<div style={{ color: "#a19f9d", fontSize: "14px", marginBottom: "16px" }}>No contact details provided.</div>
					)}
				</div>
				<div>
					<PrimaryButton
						onClick={() => setEditContact({ name: sectionTitle, type: contactType })}
						text={contactExists ? "Edit" : "Add Contact"}
						disabled={loading}
						styles={{ root: { width: "100%" } }}
					/>
				</div>
			</div>
		)
	}

	const isSaveButtonEnabled = () => {
		if (loading || saving) return false
		const isDirty = Object.keys(formData).some(contactType => {
			const contact = formData[contactType]
			const existingContact = accountData?.[contactType]
			if (contact) {
				const isComplete = contact.firstname && contact.lastname && contact.emailaddress1 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.emailaddress1) // Ensure email is valid
				const hasChanges =
					!existingContact ||
					contact.firstname !== existingContact.firstname ||
					contact.lastname !== existingContact.lastname ||
					contact.emailaddress1 !== existingContact.emailaddress1 ||
					contact.telephone1 !== existingContact.telephone1 ||
					contact.cw_businessphoneextention !== existingContact.cw_businessphoneextention ||
					contact.mobilephone !== existingContact.mobilephone
				return !!isComplete && hasChanges
			} else {
				return false
			}
		})
		return isDirty
	}

	return (
		<div style={{ maxWidth: 1000 }}>
			<Stack tokens={{ childrenGap: 8 }}>
				<Stack horizontal tokens={{ childrenGap: 16 }} wrap>
					{renderContactCard("Primary Contact", "primarycontactid")}
					{renderContactCard("Secondary Contact", "cw_SecondaryContact")}
				</Stack>
				<Stack horizontal tokens={{ childrenGap: 16 }} wrap>
					{renderContactCard("Plan Fiduciary Contact", "cw_PlanFiduciaryContact")}
					{renderContactCard("Billing Contact", "cw_BillingContact")}
				</Stack>
				<Stack horizontal tokens={{ childrenGap: 16 }} wrap styles={{ root: { maxWidth: "calc(50% - 8px)" } }}>
					{renderContactCard("Admin Contact", "cw_AdminContact")}
				</Stack>
			</Stack>
			{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			{saveError && <MessageBar messageBarType={MessageBarType.error}>{saveError}</MessageBar>}
			{editContact && (
				<Dialog hidden={false} onDismiss={() => setEditContact(undefined)} dialogContentProps={{ title: `${formData[editContact.type] ? "Editing" : "Adding"} Contact` }}>
					<Stack>{renderFormSection(editContact.name, editContact.type)}</Stack>
					<DialogFooter>
						<DefaultButton onClick={() => setEditContact(undefined)} text="Cancel" disabled={saving} />
						<PrimaryButton onClick={() => onSave(editContact.type)} disabled={!isSaveButtonEnabled()}>
							{saving ? (
								<Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
									<Spinner labelPosition="left" />
									<span>Saving...</span>
								</Stack>
							) : (
								"Save"
							)}
						</PrimaryButton>
					</DialogFooter>
				</Dialog>
			)}
		</div>
	)
}

function PanelForm(props: PanelFormProps) {
	const [loading, setLoading] = useState<boolean>(true)
	const [error, setError] = useState<string | undefined>()
	const [saving, setSaving] = useState(false)
	const { group } = props
	const addGroup = Boolean(!group)
	const [form, setForm] = useState<IAccount>(() => getDefaultState(group, addGroup))

	const originalForm = usePrevious(form)
	const [brokerageId, setBrokerageId] = useState<string | undefined>()
	const currentUserContactId = (window.top as any).parent.Microsoft.Dynamic365.Portal.User.contactId
	const [isEditMode, setIsEditMode] = useState(addGroup)
	const [showPlanSelector, setShowPlanSelector] = useState<boolean>(false)
	const [selectedPlans, setSelectedPlans] = useState<string[]>(group?.plans?.map(p => p.cw_name) || [])

	const [showDeptDialog, setShowDeptDialog] = useState<boolean>(false)
	const initialDeptState: Partial<CWDepartment> = {
		cw_name: "",
		cw_description: ""
	}
	const [newDept, setNewDept] = useState<Partial<CWDepartment>>(initialDeptState)
	const [groupDepartments, setGroupDepartments] = useState<string[]>(group?.departments?.map(d => d.cw_name) || [])
	const [availableContactsForFiltering, setAvailableContactsForFiltering] = useState<IPersonaProps[]>(form.cw_PreferredBroker ? [convertBrokerToPersona(form.cw_PreferredBroker)] : [])
	const [addtGroupInfo, setAddtGroupInfo] = useState<IAccount>(undefined)

	useEffect(() => {
		async function initialize() {
			try {
				const promises = [
					makeCRMApiCall(`contacts(${currentUserContactId})?$select=_cw_brokerage_value`),
					makeACMGCRMApiCall(`getBrokerageContactsForGroup`),
					group ? makeCRMApiCall(`accounts(${group.accountid})?$select=cw_uploadedcensus&$expand=primarycontactid($select=telephone1,emailaddress1,firstname,lastname)`) : null
				]
				const [brokerageResponse, contacts, addtGroupInfo] = await Promise.all(promises)
				setBrokerageId(brokerageResponse._cw_brokerage_value)
				setAvailableContactsForFiltering([
					...(form.cw_PreferredBroker
						? [
								{
									key: form.cw_PreferredBroker.contactid,
									text: form.cw_PreferredBroker.fullname,
									_cw_brokerage_value: brokerageResponse._cw_brokerage_value
								} as IPersonaProps
							]
						: []),
					...(contacts
						.filter(c => c.mga_contactid !== currentUserContactId && c.mga_contactid !== null && c.mga_contactid !== "")
						.map(c => ({ text: c.mga_contact_name, key: c.mga_contactid, secondaryText: c.mga_contact_email, _cw_brokerage_value: c.brokerage_id })) as IPersonaProps[])
				])
				if (addtGroupInfo) setAddtGroupInfo(addtGroupInfo)
			} catch (err) {
				console.error(err)
				setError("There was an error loading the data")
			} finally {
				setLoading(false)
			}
		}

		initialize()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []) // intentionally left empty to only run on mount

	const madeChanges = () => JSON.stringify(originalForm) !== JSON.stringify(form)

	const allIndustriesCode = window.location.hostname.includes("-sandbox") ? "f8042f4f-8a3b-ee11-bdf4-6045bdd4f94e" : "50c02517-506f-ee11-8df0-000d3a5474a4"
	const getFormSaveBody = () => {
		const body: Partial<INewAccount> = {
			name: form.name !== group?.name ? form.name : undefined,
			address1_city: form.address1_city !== group?.address1_city ? form.address1_city : undefined,
			address1_country: form.address1_country !== group?.address1_country ? form.address1_country : undefined,
			address1_line1: form.address1_line1 !== group?.address1_line1 ? form.address1_line1 : undefined,
			address1_postalcode: form.address1_postalcode !== group?.address1_postalcode ? form.address1_postalcode : undefined,
			telephone1: form.telephone1 !== group?.telephone1 ? form.telephone1 : undefined,
			cw_grouptaxidnumber: form.cw_grouptaxidnumber !== group?.cw_grouptaxidnumber ? form.cw_grouptaxidnumber : undefined,
			cw_coveragestartdate: form.cw_coveragestartdate !== group?.cw_coveragestartdate ? form.cw_coveragestartdate : undefined,
			"cw_PreferredBroker@odata.bind": form.cw_PreferredBroker?.contactid !== group?.cw_PreferredBroker?.contactid ? `/contacts(${form.cw_PreferredBroker?.contactid})` : undefined,
			cw_accounttype: addGroup ? String(121640002) : undefined,
			"cw_Brokerage@odata.bind":
				form.cw_PreferredBroker?.contactid !== group?.cw_PreferredBroker?.contactid ? `/cw_brokerages(${addGroup ? brokerageId : form.cw_PreferredBroker?._cw_brokerage_value})` : undefined,
			"cw_Industry@odata.bind": `/cw_industries(${allIndustriesCode})`, // hard coded to "All Other Approved Industries"
			address1_stateorprovince: form.address1_stateorprovince !== group?.address1_stateorprovince ? form.address1_stateorprovince : undefined,
			cw_allowmgamanagement: availableContactsForFiltering.length > 1 && !IS_ADMIN //more than 1 contact for broker choice and not admin means MGA
		}

		if (addGroup) {
			body.cw_legalname = form.name
		}

		return body
	}

	// TODO: SOME group plans fail when adding/deleting, others working fine. Very weird - maybe when/how the universal plans were added? Will sometimes throw error you do not have permission to associate or dissociate universal plans to group plans.

	const handleEditGroupPlans = async (incomingSelectedPlans: IPlanSelectorPlan[]) => {
		setSaving(true)
		setError(undefined)

		try {
			const newPlans = incomingSelectedPlans.filter(p => !group?.plans?.some(existing => existing.cw_name === p.name))
			const promises = []

			newPlans.forEach(gp => {
				promises.push(
					makeCRMApiCall(
						"cw_groupplanses",
						"POST",
						JSON.stringify({
							"cw_Group@odata.bind": `/accounts(${group?.accountid})`,
							"cw_Plan@odata.bind": `/cw_universalplanses(${gp.id})`,
							cw_name: `${gp.name}`
						})
					)
				)
			})

			const deletedPlans = group?.plans?.filter(existing => !incomingSelectedPlans.some(p => p.name === existing.cw_name))
			deletedPlans?.forEach(gp => {
				promises.push(makeCRMApiCall(`/cw_groupplanses(${gp.cw_groupplansid})`, "DELETE"))
			})

			await Promise.all(promises)

			const updatedGroup = await queryForAccount(group.accountid)

			setForm(prevForm => ({
				...prevForm,
				plans: updatedGroup.plans
			}))

			if (props.onSave) {
				props.onSave(group.accountid)
			}
		} catch (err) {
			setError(getErrorText(err))
		} finally {
			setSaving(false)
		}
	}

	const editItem = async () => {
		const body = getFormSaveBody()

		let id
		if (Object.values(body).some(v => v !== undefined)) {
			const response = await makeCRMApiCall(addGroup ? "accounts" : `accounts(${group.accountid})`, addGroup ? "POST" : "PATCH", JSON.stringify(body), undefined, ["entityid"])
			id = response?.entityid
		}

		// await handleEditGroupPlans(id)

		props.onSave(id)
		const newGroup = await queryForAccount(id)
		setForm(getDefaultState(newGroup, false))
		setIsEditMode(false)
	}

	const saveItem = async () => {
		try {
			setSaving(true)
			await editItem()
			setSaving(false)
		} catch (err) {
			setSaving(false)
			setError(getErrorText(err))
		}
	}

	const isDisabled = () =>
		!isEditMode || !form.address1_city || !form.address1_stateorprovince || !form.address1_postalcode || !form.cw_coveragestartdate || !form.cw_PreferredBroker || !form.name || saving

	const resolveBrokerSuggestions = async (filter: string, selectedItems: IPersonaProps[]) => {
		const retVal: IPersonaProps[] = availableContactsForFiltering
			.filter(c => c.text.toLowerCase().includes(filter.toLowerCase()))
			.filter(p_1 => !selectedItems.map(si => si.secondaryText).includes(p_1.secondaryText))
		return retVal
	}

	function getTextFieldStyle(): Partial<ITextFieldStyles> {
		return !isEditMode
			? {
					fieldGroup: {
						border: "none"
					},
					field: {
						backgroundColor: "#fff",
						color: "#000",
						paddingLeft: 0
					},
					subComponentStyles: {
						label: {
							root: {
								fontSize: "medium",
								color: "#000"
							}
						}
					}
				}
			: {}
	}

	const renderMilestone = (label: string, value: boolean) => (
		<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="baseline" key={label}>
			<Icon iconName={value ? "CheckboxComposite" : "Checkbox"} />
			<Label>{label}:</Label>
		</Stack>
	)

	const addDepartment = async () => {
		if (!newDept.cw_name.trim() || !newDept.cw_description.trim()) return
		setSaving(true)

		try {
			await makeCRMApiCall(
				"cw_departmentses",
				"POST",
				JSON.stringify({ cw_name: newDept.cw_name, cw_description: newDept.cw_description, "cw_Group@odata.bind": `/accounts(${group.accountid})` })
			)
			setGroupDepartments([...groupDepartments, newDept.cw_name])

			setNewDept(initialDeptState)
			setShowDeptDialog(false)
		} catch (err) {
			console.error("Failed to add department", err)
		} finally {
			setSaving(false)
		}
	}

	const onDeptFieldChange = (fieldName: keyof CWDepartment, value: string) => {
		setNewDept(prev => ({
			...prev,
			[fieldName]: value
		}))
	}

	const shouldShowEditButton = IS_ADMIN || (group && !group.cw_completedcompanyinfo && !group.cw_salelost)

	const convertPlanSelectorPlanToGroupPlan = (planSelection: IPlanSelectorPlan): IGroupPlan => ({ cw_universalplansid: planSelection.id, cw_name: planSelection.name }) as IGroupPlan
	const canEditPlans = IS_ADMIN || !group?.cw_lockunderwriting || !group?.cw_markassold || !group?.cw_salelost

	const isPrimaryContactComplete = (contact: any) => {
		if (!contact) return false
		const hasNames = contact.firstname?.trim() && contact.lastname?.trim()
		const hasContactInfo = contact.emailaddress1 && contact.telephone1
		return hasNames && hasContactInfo
	}

	const getCompletionStatus = () => {
		const requiredFields = [
			{ label: "Company Name", value: form.name },
			{ label: "Broker", value: form.cw_PreferredBroker },
			{ label: "Effective Date", value: form.cw_coveragestartdate },
			{ label: "City", value: form.address1_city },
			{ label: "State", value: form.address1_stateorprovince },
			{ label: "Zip Code", value: form.address1_postalcode },
			{ label: "Primary Contact Info", value: isPrimaryContactComplete(addtGroupInfo?.primarycontactid) },
			{ label: "Uploaded Census", value: addtGroupInfo?.cw_uploadedcensus }
		]

		const completed = requiredFields.filter(f => !!f.value)
		const percent = Math.round((completed.length / requiredFields.length) * 100)

		return { requiredFields, percent, isComplete: completed.length === requiredFields.length }
	}

	const { requiredFields, percent, isComplete } = getCompletionStatus()

	return (
		<Stack horizontal tokens={{ childrenGap: 40 }} styles={{ root: { padding: 20 } }}>
			<Stack tokens={{ childrenGap: 12 }} style={{ maxWidth: 500 }}>
				<Stack>
					<Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center" grow>
						<Stack tokens={{ childrenGap: 12 }}>
							<Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center" grow>
								<Stack.Item>
									<h3 style={h3Styles}>Group Details</h3>
									{group && shouldShowEditButton && <PrimaryButton text={isEditMode ? "Disable Edit" : "Enable Edit"} onClick={() => setIsEditMode(!isEditMode)} />}
								</Stack.Item>
							</Stack>
							<Stack horizontal tokens={{ childrenGap: 12 }}>
								<Stack styles={{ root: { minWidth: "25rem" } }}>
									<TextField
										styles={getTextFieldStyle()}
										disabled={!isEditMode}
										label="Company Name"
										required={isEditMode}
										value={form.name as string}
										onChange={(_, newValue) => handleChange("name", newValue || "", setForm)}
									/>
									<TextField
										styles={getTextFieldStyle()}
										disabled={!isEditMode}
										label="Address"
										multiline
										value={(form.address1_line1 as string) || ""}
										onChange={(_, newValue) => handleChange("address1_line1", newValue || "", setForm)}
									/>
									<TextField
										styles={getTextFieldStyle()}
										disabled={!isEditMode}
										label="City"
										required={isEditMode}
										value={form.address1_city as string}
										onChange={(_, newValue) => handleChange("address1_city", newValue || "", setForm)}
									/>
								</Stack>
								<Stack styles={{ root: { minWidth: "25rem" } }}>
									{!addGroup && (
										<TextField
											styles={getTextFieldStyle()}
											disabled={!isEditMode || !IS_ADMIN}
											label="Group Code"
											value={form.cw_groupcode as string}
											onChange={(_, newValue) => handleChange("cw_groupcode", newValue || "", setForm)}
										/>
									)}
									<TextField
										styles={getTextFieldStyle()}
										disabled={!isEditMode}
										label="Tel #"
										value={form.telephone1 as string}
										onChange={(_, newValue) => handleChange("telephone1", newValue || "", setForm)}
									/>
									<Stack horizontal styles={{ root: { marginTop: "auto" } }}>
										<TextField
											disabled={!isEditMode}
											label="State"
											styles={{ ...getTextFieldStyle(), root: { width: "8rem", marginRight: "2rem" } }}
											required={isEditMode}
											value={(form.address1_stateorprovince as string) || ""}
											onChange={(_, newValue) => handleChange("address1_stateorprovince", newValue || "", setForm)}
										/>
										<TextField
											disabled={!isEditMode}
											styles={{ ...getTextFieldStyle(), root: { width: "100%" } }}
											label="Zip"
											required={isEditMode}
											value={(form.address1_postalcode as string) || ""}
											onChange={(_, newValue) => handleChange("address1_postalcode", newValue || "", setForm)}
										/>
									</Stack>
									<TextField
										styles={getTextFieldStyle()}
										disabled={!isEditMode}
										label="Tax ID Number"
										value={form.cw_grouptaxidnumber as string}
										onChange={(_, newValue) => handleChange("cw_grouptaxidnumber", newValue || "", setForm)}
									/>
								</Stack>
							</Stack>
						</Stack>
						{/* {IS_ADMIN && group && (
							<Stack>
								<h3 style={h3Styles}>Milestones</h3>
								<Stack styles={{ root: { maxWidth: "100%", minWidth: "25rem" } }}>
									{[
										{ label: "Completed Company Info", value: group.cw_completedcompanyinfo },
										{ label: "Locked Census", value: group.cw_lockedcensus },
										{ label: "Lock Underwriting", value: group.cw_lockunderwriting },
										{ label: "Quoted", value: group.cw_quoted },
										{ label: "Mark as Sold", value: group.cw_markassold },
										{ label: "Sale Lost", value: group.cw_salelost },
										{ label: "Marked as Terminated", value: group.cw_markedasterminated },
										{ label: "Group Denied Underwriting", value: group.cw_groupdeniedunderwriting },
										{ label: "Start Enrollment for Qualifying Events", value: group.cw_startenrollmentforqualifyingevents }
									].map(milestone => renderMilestone(milestone.label, milestone.value))}
								</Stack>
							</Stack>
						)} */}
					</Stack>
				</Stack>
				<Stack>
					<h3 style={h3Styles}>Coverage Details</h3>
					<Stack styles={{ root: { maxWidth: "100%", minWidth: "25rem" } }}>
						{!isEditMode && (
							<Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
								<Label>Industry SIC Code: </Label>
								<span>{group?.cw_Industry?.cw_name}</span>
							</Stack>
						)}
						<Stack.Item key={form.cw_PreferredBroker?.contactid}>
							<Label required={isEditMode}>
								{isEditMode && "Assign Preferred "}Broker{!isEditMode && `: ${form.cw_PreferredBroker?.fullname || "N/A"}`}
							</Label>
							{isEditMode && (
								<NormalPeoplePicker
									disabled={(!isEditMode || !IS_ADMIN) && availableContactsForFiltering.length <= 1}
									selectedItems={form.cw_PreferredBroker ? [convertBrokerToPersona(form.cw_PreferredBroker)] : undefined}
									itemLimit={1}
									onResolveSuggestions={resolveBrokerSuggestions}
									onChange={(items?: IPersonaProps[]) => {
										handleChange("cw_PreferredBroker", items?.length > 0 ? convertPersonaToBroker(items[0]) : undefined, setForm)
										setBrokerageId((items?.[0] as any)?._cw_brokerage_value)
									}}
								/>
							)}
						</Stack.Item>
						{!addGroup && (
							<Stack.Item style={{ marginTop: 4 }}>
								<Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
									<Label>Plans:</Label>
									<span>{selectedPlans?.join(", ") || "None"}</span>
								</Stack>

								{isEditMode && canEditPlans && IS_ADMIN && (
									<PrimaryButton
										disabled={!isEditMode}
										text="Select Plans"
										onClick={() => {
											setShowPlanSelector(true)
										}}
									/>
								)}
							</Stack.Item>
						)}
						<Stack.Item>
							<div style={{ height: "auto" }}>
								<Label required={isEditMode}>
									Coverage Start Date
									<span style={{ fontWeight: "normal" }}>{form.cw_coveragestartdate && `: ${safeFormatDateValue(form.cw_coveragestartdate, "MM/dd/yyyy", "Invalid Date")}`}</span>
								</Label>
								{(addGroup || isEditMode) && canEditPlans && (
									<DatePicker
										calendarProps={{ dateRangeType: DateRangeType.Month, isDayPickerVisible: false }}
										minDate={minDate}
										value={form.cw_coveragestartdate ? new Date(form.cw_coveragestartdate.replace(/.$/, "2")) : undefined} // if you don't replace the final character, the date evaulates incorrectly to the previous month
										onSelectDate={date => {
											const firstDay = new Date(date.getFullYear(), date.getMonth(), 1)
											handleChange("cw_coveragestartdate", firstDay?.toISOString().substring(0, 10), setForm)
										}}
										strings={defaultCalendarStrings}
										formatDate={date => {
											return date.toLocaleDateString(undefined, { month: "long", year: "numeric" })
										}}
									/>
								)}
							</div>
						</Stack.Item>
						{!addGroup && (
							<Stack.Item style={{ marginTop: 4 }}>
								<Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
									<Label>Departments:</Label>
									<span>{groupDepartments?.join(", ") || "N/A"}</span>
								</Stack>
								{isEditMode && IS_ADMIN && (
									<PrimaryButton
										text="Add a Department"
										onClick={() => {
											setShowDeptDialog(true)
										}}
									/>
								)}
							</Stack.Item>
						)}
					</Stack>
				</Stack>
				<Stack tokens={{ childrenGap: 10 }}>
					{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
					{saving && <ProgressIndicator label="This may take a minute" />}
					{isEditMode && (
						<Stack horizontal tokens={{ childrenGap: 10 }}>
							<PrimaryButton onClick={saveItem} disabled={isDisabled() || !madeChanges()}>
								{addGroup ? "Create Group" : "Save Changes"}
							</PrimaryButton>
						</Stack>
					)}
				</Stack>
				{showPlanSelector && (
					<PlanSelector
						onDismiss={() => setShowPlanSelector(false)}
						onSave={plans => {
							setSelectedPlans(plans.map(p => p.name))
							setShowPlanSelector(false)
							handleEditGroupPlans(plans)
						}}
						defaultSelectedPlans={selectedPlans || []}
						displayOnly={false}
					/>
				)}
				{showDeptDialog && (
					<Dialog
						hidden={!showDeptDialog}
						onDismiss={() => setShowDeptDialog(false)}
						dialogContentProps={{ title: "Add New Department", subText: "Enter the name of the department you wish to add to this group." }}>
						<TextField label="Department Code" required value={newDept.cw_name} onChange={(_, val) => onDeptFieldChange("cw_name", val || "")} />
						<TextField label="Description" required value={newDept.cw_description} onChange={(_, val) => onDeptFieldChange("cw_description", val || "")} />
						<DialogFooter>
							<PrimaryButton onClick={addDepartment} text="Add" disabled={saving || newDept.cw_name === "" || newDept.cw_description === ""} />
							<DefaultButton onClick={() => setShowDeptDialog(false)} text="Cancel" disabled={saving} />
						</DialogFooter>
					</Dialog>
				)}
			</Stack>
			<Stack>
				{!addGroup && (
					<div>
						<h3>Setup Progress</h3>
						{!loading && <ProgressIndicator percentComplete={percent / 100} label={`${percent}% Complete`} description={isComplete ? "Ready for quoting" : "Missing required info"} />}
						<Stack>
							{loading ? (
								<Spinner />
							) : (
								requiredFields.map((field, i) => (
									<Stack horizontal verticalAlign="center" key={i} tokens={{ childrenGap: 8 }}>
										<Icon iconName={field.value ? "CheckMark" : "CircleRing"} styles={{ root: { color: field.value ? "#107c10" : "#a19f9d", fontWeight: "bold" } }} />
										<span style={{ color: field.value ? "#000" : "#605e5c" }}>{field.label}</span>
									</Stack>
								))
							)}
						</Stack>
					</div>
				)}
			</Stack>
		</Stack>
	)
}
